class WallpaperCalculator extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.wallpaper-calc__trigger');
    this.overlay = this.querySelector('.wallpaper-calc__overlay');
    this.popup = this.querySelector('.wallpaper-calc__popup');
    this.closeBtn = this.querySelector('.wallpaper-calc__close');
    this.form = this.querySelector('.wallpaper-calc__form');
    this.widthInput = this.querySelector('#wallpaper-calc-width');
    this.heightInput = this.querySelector('#wallpaper-calc-height');
    this.resultDiv = this.querySelector('.wallpaper-calc__result');
    this.resultNumber = this.querySelector('.wallpaper-calc__result-number');
    this.errorDiv = this.querySelector('.wallpaper-calc__error');
    this.successDiv = this.querySelector('.wallpaper-calc__success');
    this.noMeasurementsDiv = this.querySelector('.wallpaper-calc__no-measurements');
    this.detailWall = this.querySelector('.wallpaper-calc__detail-wall');
    this.detailRoll = this.querySelector('.wallpaper-calc__detail-roll');
    this.detailPattern = this.querySelector('.wallpaper-calc__detail-pattern');
    this.addToCartBtn = this.querySelector('.wallpaper-calc__add-to-cart');

    this.rollWidth = this.getAttribute('roll-width') ? parseFloat(this.getAttribute('roll-width')) : null;
    this.rollLength = this.getAttribute('roll-length') ? parseFloat(this.getAttribute('roll-length')) : null;
    this.patternRepeat = this.getAttribute('pattern-repeat') ? parseFloat(this.getAttribute('pattern-repeat')) : 0;
    // Trim allowance is set in mm on the block; default 100mm
    const trimMm = parseFloat(this.getAttribute('trim-allowance'));
    this.trimAllowance = (isNaN(trimMm) ? 100 : trimMm) / 1000;
    // round_up | add_full | none — set on the block
    this.patternMethod = this.getAttribute('pattern-method') || 'round_up';
    this.wastagePercent = parseFloat(this.getAttribute('wastage-percent')) || 0;
    // The first drop on the wall has nothing to match, so it can skip the pattern allowance
    this.firstDropNoPattern = this.getAttribute('first-drop-no-pattern') !== 'false';
    this.calculatedRolls = 0;
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.measurementsFound = !!(this.rollWidth && this.rollLength);
  }

  connectedCallback() {
    this.setupEventListeners();
    if (this.measurementsFound) {
      this.noMeasurementsDiv.classList.remove('wallpaper-calc__no-measurements--active');
    } else {
      this.noMeasurementsDiv.classList.add('wallpaper-calc__no-measurements--active');
      if (this.form) this.form.style.display = 'none';
    }
  }

  setupEventListeners() {
    if (this.trigger) this.trigger.addEventListener('click', () => this.openPopup());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closePopup());
    if (this.overlay) this.overlay.addEventListener('click', () => this.closePopup());
    if (this.form) this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    if (this.addToCartBtn) this.addToCartBtn.addEventListener('click', () => this.addToCart());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup && this.popup.classList.contains('wallpaper-calc__popup--active')) {
        this.closePopup();
      }
    });
  }

  openPopup() {
    if (this.overlay) this.overlay.classList.add('wallpaper-calc__overlay--active');
    if (this.popup) this.popup.classList.add('wallpaper-calc__popup--active');
    document.body.style.overflow = 'hidden';
    if (this.measurementsFound) {
      this.noMeasurementsDiv.classList.remove('wallpaper-calc__no-measurements--active');
      if (this.form) this.form.style.display = 'flex';
      if (this.widthInput) this.widthInput.focus();
    } else {
      this.noMeasurementsDiv.classList.add('wallpaper-calc__no-measurements--active');
      if (this.form) this.form.style.display = 'none';
    }
  }

  closePopup() {
    if (this.overlay) this.overlay.classList.remove('wallpaper-calc__overlay--active');
    if (this.popup) this.popup.classList.remove('wallpaper-calc__popup--active');
    document.body.style.overflow = '';
  }

  handleSubmit(e) {
    e.preventDefault();
    if (!this.measurementsFound) {
      this.showError('Product roll measurements are missing.');
      return;
    }
    this.errorDiv.classList.remove('wallpaper-calc__error--active');
    this.successDiv.classList.remove('wallpaper-calc__success--active');
    this.resultDiv.classList.remove('wallpaper-calc__result--active');

    const wallWidth = parseFloat(this.widthInput.value);
    const wallHeight = parseFloat(this.heightInput.value);

    if (!wallWidth || !wallHeight || wallWidth <= 0 || wallHeight <= 0) {
      this.showError('Please enter valid wall dimensions.');
      return;
    }

    const rolls = this.calculateRolls(wallWidth, wallHeight, this.rollWidth, this.rollLength, this.patternRepeat);
    if (!isFinite(rolls)) {
      this.showError('This wall is taller than a single roll. Please contact us for help with your order.');
      return;
    }
    this.calculatedRolls = rolls;
    this.displayResult(rolls, wallWidth, wallHeight, this.rollWidth, this.rollLength, this.patternRepeat);
  }

  calculateRolls(wallWidth, wallHeight, rollWidth, rollLength, patternRepeat) {
    // Each drop = wall height + trim, then allow for the pattern repeat
    const neededLength = wallHeight + this.trimAllowance;
    let effectiveDropLength = neededLength;
    if (patternRepeat > 0 && this.patternMethod === 'round_up') {
      // Round up to the next full repeat so every drop starts at the same point in the pattern
      effectiveDropLength = Math.ceil(Math.round((neededLength / patternRepeat) * 1000) / 1000) * patternRepeat;
    } else if (patternRepeat > 0 && this.patternMethod === 'add_full') {
      effectiveDropLength = neededLength + patternRepeat;
    }
    // Drop 1 is wall height + trim only; every later drop includes the pattern allowance
    const firstDropLength = this.firstDropNoPattern ? neededLength : effectiveDropLength;
    // Round to 3dp before flooring so float error (e.g. 2.9999) doesn't lose a drop
    const fits = (length, drop) => Math.floor(Math.round((length / drop) * 1000) / 1000);
    const stripsNeeded = Math.ceil(Math.round((wallWidth / rollWidth) * 1000) / 1000);
    if (firstDropLength > rollLength) return Infinity;

    // First roll holds drop 1 plus as many full drops as fit in what's left
    const firstRollStrips = 1 + fits(rollLength - firstDropLength, effectiveDropLength);
    const remainingStrips = Math.max(0, stripsNeeded - firstRollStrips);
    const stripsPerRoll = fits(rollLength, effectiveDropLength);
    if (remainingStrips > 0 && stripsPerRoll === 0) return Infinity;

    const rolls = 1 + (remainingStrips > 0 ? Math.ceil(remainingStrips / stripsPerRoll) : 0);
    const rollsWithWastage = rolls * (1 + this.wastagePercent / 100);
    return Math.ceil(Math.round(rollsWithWastage * 1000) / 1000);
  }

  displayResult(rolls, wallWidth, wallHeight, rollWidth, rollLength, patternRepeat) {
    this.resultNumber.textContent = rolls;
    this.resultDiv.classList.add('wallpaper-calc__result--active');
    this.detailWall.textContent = `Wall: ${wallWidth}m wide × ${wallHeight}m high`;
    this.detailRoll.textContent = `Roll: ${rollWidth}m wide × ${rollLength}m long`;
    this.detailPattern.textContent = `Pattern repeat: ${patternRepeat * 100}cm`;
    this.addToCartBtn.textContent = `Add ${rolls} rolls to cart`;
  }

  async addToCart() {
    if (this.calculatedRolls <= 0) {
      this.showError('Please calculate the number of rolls first.');
      return;
    }
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Adding to cart...';
    this.errorDiv.classList.remove('wallpaper-calc__error--active');
    this.successDiv.classList.remove('wallpaper-calc__success--active');

    try {
      // Step 1: Add to cart (no sections — matches order-sample-wrapper pattern)
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(this.variantId), quantity: this.calculatedRolls }] }),
      });

      const addedItem = await addResponse.json();

      if (addedItem.status) {
        throw new Error(addedItem.description || addedItem.message || 'Failed to add to cart');
      }

      this.successDiv.classList.add('wallpaper-calc__success--active');
      this.addToCartBtn.textContent = 'Added to cart';

      // Step 2: Fetch section HTML separately, then renderContents
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        const sectionIds = cart.getSectionsToRender().map((s) => s.id);
        const sectionData = await fetch('/?sections=' + sectionIds.join(',')).then((r) => r.json());

        // Must remove is-empty BEFORE renderContents (renderContents only clears it
        // from .drawer__inner, not from the cart-drawer element itself)
        cart.classList.remove('is-empty');
        cart.renderContents({ id: addedItem.id, sections: sectionData });
      }

      setTimeout(() => {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = `Add ${this.calculatedRolls} rolls to cart`;
      }, 2000);
    } catch (err) {
      this.showError(err.message || 'Unable to add to cart. Please try again.');
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.textContent = `Add ${this.calculatedRolls} rolls to cart`;
    }
  }

  showError(message) {
    this.errorDiv.textContent = message;
    this.errorDiv.classList.add('wallpaper-calc__error--active');
  }
}

if (!customElements.get('wallpaper-calculator')) {
  customElements.define('wallpaper-calculator', WallpaperCalculator);
}
